import {
	IEnhancedMapping,
	IMap,
	IMarker,
	MarkerType,
	ILatLng,
	ILatLngBounds,
	extend,
	union
} from "@sureview/v2-mapping"

import "@/scripts/array-operations"

export enum MarkerDrawType {
	Point,
	Path,
	Region
}

export const MarkerColors = [
	"Blue",
	"Green",
	"Orange",
	"Purple",
	"Red"
]

export const markerColorData: { [key: string]: string } = {
	blue: "#0b78ce",
	green: "#4CAF50",
	orange: "#df520a",
	purple: "#9C27B0",
	red: "#e51c23"
}

export interface DrawArguments {
	markerText: string;
	drawType: MarkerDrawType;
	colorName: string;
	color: string;
	set: (value: any) => void;
	cancel: () => void;
}

interface FieldValues
{
	remove: () => void;
	bounds: () => ILatLngBounds | null;
}

interface FieldMapValue
{
	drawType: MarkerDrawType,
	title: string,
	color: string,
	value: any;
	markers: FieldValues | null
}

const baseLocationImagesFolder: string = "./images/map/"

export class MapProvider {
	public mapping: IEnhancedMapping | null = null;
	public map: IMap | null = null;
	public mapElement: HTMLElement | null = null;
	public boundsSet: boolean = false;
	public mapStyle: any = {};

	private handlers: { [event: string]: ((arg: any) => void)[] } = {};
	private fieldValues: { [fieldId: string]: FieldMapValue } = {}

	/**
	 * Configure mapping object for provider
	 * @param mapping Enhanced Mapping provider
	 * @param map Map object
	 */
	public setMapping (mapping: IEnhancedMapping, map: IMap) {
		this.mapping = mapping
		this.map = map

		Object.keys(this.fieldValues).forEach(fieldId => {
			if (!this.fieldValues[fieldId].markers) {
				this.fieldValues[fieldId].markers = this.setValueOnMap(
					this.fieldValues[fieldId].drawType,
					this.fieldValues[fieldId].color,
					this.fieldValues[fieldId].title,
					this.fieldValues[fieldId].value
				)
			}
		})

		this.fitToBounds()
	}

	/**
	 * Resets map to element.
	 */
	public resetMap () {
		if (this.mapElement) {
			this.map = this.mapping!.createMap(
				this.mapElement,
				this.map!.center,
				0,
				false,
				"roadmap",
				this.mapStyle
			)

			this.fitToBounds()
		}
	}

	/**
	 * Add event handler
	 * @param event Event type
	 * @param handler Event handler function
	 */
	public on (event: string, handler: (arg: any) => void): void {
		if (!this.handlers[event]) {
			this.handlers[event] = []
		}

		this.handlers[event].push(handler)
	}

	/**
	 * Remove event handler
	 * @param event Event type
	 * @param handler Event handler function
	 */
	public off (event: string, handler: (arg: any) => void): void {
		if (this.handlers[event]) {
			this.handlers[event].remove(handler)
		}
	}

	/**
	 * Remove all event handlers
	 */
	public clear () {
		this.handlers = {}
	}

	/**
	 * Remove all markers from map
	 */
	public clearMarkers () {
		Object.keys(this.fieldValues).forEach(fieldId => {
			if (this.fieldValues[fieldId].markers) {
				this.fieldValues[fieldId].markers!.remove()
			}
		})
		this.fieldValues = {}
	}

	/**
	 * Scrolls map into view and raises startDraw event
	 * @param args Map drawing arguments to raise
	 */
	public startDraw (args: DrawArguments) {
		args.color = markerColorData[args.colorName.toLowerCase()]

		if (this.mapElement) {
			this.mapElement.scrollIntoView()
		}

		this.raiseEvent("startDraw", args)
	}

	/**
	 * Scrolls map into view and centers map on location
	 * @param location Point or array of points to center on
	 */
	public goto (location: ILatLng | ILatLng[]) {
		if (!this.map) return

		if (this.mapElement) {
			this.mapElement.scrollIntoView()
		}

		if (Array.isArray(location)) {
			const pointsBounds = this.pointsUnion(location)
			if (pointsBounds) {
				this.map!.mapBounds = pointsBounds
			}
		} else {
			this.map!.center = location
			this.map!.zoom = location.zoom ? location.zoom : 19
		}
	}

	/**
	 * Draw value on map
	 * @param fieldId Field ID to draw
	 * @param drawType Type of item to create
	 * @param fieldTitle Title of field on map
	 * @param color Color for map item
	 * @param value Field value
	 */
	public drawValue (fieldId: string, drawType: MarkerDrawType, fieldTitle: string, color: string, value: any) {
		if (this.mapping) {
			if (this.fieldValues[fieldId] && this.fieldValues[fieldId].markers) {
				this.fieldValues[fieldId].markers!.remove()
			}

			this.fieldValues[fieldId] = {
				drawType,
				title: fieldTitle,
				color,
				value,
				markers: this.setValueOnMap(drawType, color, fieldTitle, value)
			}
		} else {
			this.fieldValues[fieldId] = {
				drawType,
				title: fieldTitle,
				color,
				value,
				markers: null
			}
		}
	}

	private fitToBounds () {
		let bounds = {} as ILatLngBounds
		let boundsSet = false

		Object.keys(this.fieldValues).forEach(fieldId => {
			if (this.fieldValues[fieldId].markers) {
				const fieldBounds = this.fieldValues[fieldId].markers!.bounds()

				if (fieldBounds) {
					bounds = union(bounds, fieldBounds)
					boundsSet = true
				}
			}
		})

		if (boundsSet) {
			this.map!.mapBounds = bounds
			this.boundsSet = true
		}
	}

	/**
	 * Create mapping object for field value
	 * @param drawType Type of field (Point / Path / Region)
	 * @param color Selected color
	 * @param fieldTitle Title of field
	 * @param value Current value
	 */
	private setValueOnMap (drawType: MarkerDrawType, color: string, fieldTitle: string, value: any): FieldValues | null {
		switch (drawType) {
			case MarkerDrawType.Point:
				const markerType = new MarkerType({
					imageUrl: baseLocationImagesFolder + color + ".svg"
				}, null)
				const markers: IMarker[] = value.map((v: any, index: number) =>
					this.createMarker(markerType,
						fieldTitle + (value.length > 1 ? " (" + (index + 1).toString() + ")" : ""), v))

				return {
					markers: markers,
					remove: () => {
						markers.forEach(marker => {
							marker.remove()
						})
					},
					bounds: () => {
						if (markers.length === 0) return null

						let bounds = {} as ILatLngBounds
						markers.forEach(marker => {
							bounds = extend(bounds, marker.position)
						})
						return bounds
					}
				} as FieldValues
			case MarkerDrawType.Region:
				const regionMarkerType = new MarkerType({
					line: {
						color: markerColorData[color.toLowerCase()],
						opacity: 1,
						weight: 2
					},
					fill: {
						color: markerColorData[color.toLowerCase()],
						opacity: 0.3
					}
				}, null)
				const regions = value.map((region: any) => this.mapping!.createPolygon(this.map!,
					region,
					regionMarkerType,
					() => this.goto(region)))
				return {
					regions,
					remove: () => {
						regions.forEach((region: any) => {
							region.remove()
						})
					},
					bounds: () => {
						if (value.length === 0) return null
						if (!value.some((regionPath: ILatLng[]) => regionPath.length > 0)) return null

						let bounds = {} as ILatLngBounds
						value.forEach((regionPath: ILatLng[]) => {
							const pointsUnion = this.pointsUnion(regionPath)
							if (pointsUnion) {
								bounds = union(bounds, pointsUnion!)
							}
						})
						return bounds
					}
				} as FieldValues
			case MarkerDrawType.Path:
				const pathMarkerType = new MarkerType({
					line: {
						color: markerColorData[color.toLowerCase()],
						opacity: 1,
						weight: 2
					}
				}, null)

				const paths = value.map((path: any) => this.mapping!.createPolyline(this.map!,
					path,
					pathMarkerType,
					() => this.goto(path)))

				return {
					paths,
					remove: () => {
						paths.forEach((path: any) => {
							path.remove()
						})
					},
					bounds: () => {
						if (value.length === 0) return null
						if (!value.some((pathPoints: ILatLng[]) => pathPoints.length > 0)) return null

						let bounds = {} as ILatLngBounds
						value.forEach((pathPoints: ILatLng[]) => {
							const pointsUnion = this.pointsUnion(pathPoints)
							if (pointsUnion) {
								bounds = union(bounds, pointsUnion!)
							}
						})
						return bounds
					}
				} as FieldValues
		}

		return null
	}

	private createMarker (markerType: MarkerType, title: string, location: ILatLng): IMarker {
		const marker = this.mapping!.createMarker(this.map!,
			null,
			null,
			markerType,
			null,
			title,
			location,
			null,
			null, null,
			true
		)

		marker.click = () => this.goto(location)

		return marker
	}

	private raiseEvent (event: string, args?: any) {
		if (this.handlers[event]) {
			this.handlers[event].forEach(handler => {
				handler(args)
			})
		}
	}

	private pointsUnion (points: ILatLng[]): ILatLngBounds | null {
		let bounds: ILatLngBounds | null = null
		points.forEach(vertexPosition => {
			bounds = extend(bounds || {} as ILatLngBounds, vertexPosition)
		})
		return bounds
	}
}
